import "./publicPath";
import "theme/main.scss";
import importPolyfills from "./polyfill";
import React from "react";
import ReactDOM from "react-dom";
import makeApolloClient from "web/core/apollo/makeApolloClient";
import logHandler from "web/core/logs/logHandler";
import { BrowserRouter } from "react-router-dom";
import { loadableReady } from "@loadable/component";
logHandler.initialize();

let helmetContext = {};
let apolloClient = null;
const appContainer = document.getElementById("app");

try {
  const renderStandaloneComponent = (element) => {
    const makeApp = require("./makeApp").default;
    const device =
      require("theme/components/helpers/MediaQuery").getSSRDevice();
    const App = makeApp(apolloClient, helmetContext, device, () => element);
    ReactDOM.render(<App shop={window.shopConfig} />, appContainer);
  };

  const renderMaintenance = () => {
    const Maintenance = require("theme/pages/Maintenance").default;
    renderStandaloneComponent(<Maintenance />);
  };

  const renderOffline = () => {
    const Offline = require("theme/pages/Offline").default;
    renderStandaloneComponent(<Offline />);
  };

  const getApolloClient = async () => {
    if (!apolloClient) {
      const useGETForHashedQueries =
        process.env.FRONT_COMMERCE_WEB_USE_GET_FOR_PERSISTED_QUERIES === "true";
      const HttpLink = useGETForHashedQueries
        ? await import("apollo-link-http").then(({ HttpLink }) => HttpLink)
        : await import("apollo-link-batch-http").then(
            ({ BatchHttpLink }) => BatchHttpLink
          );
      const httpLink = new HttpLink({
        uri: window.__BASE_URL__ + "/graphql",
        credentials: "same-origin",
      });
      apolloClient = makeApolloClient({
        link: httpLink,
        onMaintenanceError: () => renderMaintenance(),
      });
    }

    return apolloClient;
  };

  const renderApp = async () => {
    const AppRouter = require("theme/modules/Router/Router.js").default;
    const makeApp = require("./makeApp").default;
    const apolloClient = await getApolloClient();
    const device =
      require("theme/components/helpers/MediaQuery").getSSRDevice();

    if (window.__OFFLINE__ === true) {
      renderOffline();
      return;
    }
    if (window.__MAINTENANCE__ === true) {
      renderMaintenance();
      return;
    }

    loadableReady(() => {
      try {
        const App = makeApp(apolloClient, helmetContext, device, (props) => (
          <BrowserRouter basename={window.__BASE_URL__}>
            <AppRouter {...props} />
          </BrowserRouter>
        ));

        const shouldHydrate = window.__HYDRATE__ ?? true;
        if (window.__SSR_FALLBACK__ === true) {
          if (
            process.env.NODE_ENV === "development" &&
            window.FRONT_COMMERCE_DEV_SSR_FALLBACK_DISABLE !== true
          ) {
            const button = document.querySelector("#front-commerce-ssr-button");
            const buttonListener = (event) => {
              ReactDOM.render(<App />, appContainer);
              button.removeEventListener("click", buttonListener);
            };
            button.addEventListener("click", buttonListener);

            const escListener = (event) => {
              if (event.key === "Escape" || event.key === "Esc") {
                buttonListener();
                document.removeEventListener("keyup", escListener);
              }
            };
            document.addEventListener("keyup", escListener);
          } else {
            ReactDOM.render(<App />, appContainer);
          }
        } else if (shouldHydrate) {
          ReactDOM.hydrate(<App />, appContainer);
        }
      } catch (e) {
        console.error(e);
        throw e;
      }
    });
  };

  const runApp = async () => {
    await renderApp();

    if (module.hot) {
      module.hot.accept("./makeApp.js", () => {
        renderApp();
      });
      module.hot.accept("./Routes.js", () => {
        renderApp();
      });
      // Makes the mini extract css plugin work
      module.hot.accept(undefined, () => {});
    }
  };

  importPolyfills.then(() => runApp());
} catch (e) {
  if (!process.env.SERVER) {
    logHandler.getLogger().error(e);
  }
}

if ("serviceWorker" in navigator && process.env.NODE_ENV !== "development") {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(
      window.__BASE_URL__ + "/service-worker.js"
    );
  });
}

// Dynamic import for deferred styles - creates separate chunk
const loadDeferredStyles = () => {
  return import(/* webpackChunkName: "deferred-styles" */ "theme/defered.scss");
};

// Load when needed (e.g., when footer is visible)
if (typeof window !== "undefined") {
  // Load after initial render
  setTimeout(() => {
    loadDeferredStyles();
  }, 1000);
}
